<template>
  <div class="w-100" v-if="images.length > 0">
    <!-- 4+ images: main + secondary + stacked pair -->
    <div v-if="images.length >= 4" class="be-gallery-grid">
      <div class="be-gallery-main" @click="openLightbox(0)">
        <img :src="imageSrc(images[0])" :alt="getMerchant.name" class="be-gallery-img" />
      </div>
      <div class="be-gallery-secondary" @click="openLightbox(1)">
        <img :src="imageSrc(images[1])" :alt="getMerchant.name" class="be-gallery-img" />
      </div>
      <div class="be-gallery-stack">
        <div
          v-for="(image, index) in stackImages"
          :key="index"
          class="be-gallery-stack-item"
          @click="openLightbox(index + 2)">
          <img :src="imageSrc(image)" :alt="getMerchant.name" class="be-gallery-img" />
          <button
            v-if="isLastStack(index)"
            type="button"
            class="be-gallery-showall be-press"
            @click.stop="openLightbox(0)">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" stroke-width="1.8" />
              <path d="M3 15l4-4 4 4 4-5 6 6" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
            <span class="ml-2">{{ $t('general.view-all') }}</span>
          </button>
        </div>
      </div>
    </div>

    <!-- fewer than 4: single row -->
    <div v-else class="be-gallery-simple">
      <div
        v-for="(image, index) in images"
        :key="index"
        class="be-gallery-simple-item"
        @click="openLightbox(index)">
        <img :src="imageSrc(image)" :alt="getMerchant.name" class="be-gallery-img" />
        <button
          v-if="index === images.length - 1"
          type="button"
          class="be-gallery-showall be-press"
          @click.stop="openLightbox(0)">
          <span>{{ $t('general.view-all') }} ({{ images.length }})</span>
        </button>
      </div>
    </div>

    <CoolLightBox
      :items="images"
      srcName="image_url"
      :index="activeIndex"
      closeOnClickOutsideMobile
      @close="activeIndex = null" />
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import CoolLightBox from 'vue-cool-lightbox';
import 'vue-cool-lightbox/dist/vue-cool-lightbox.min.css';

const STACK_START = 2;
const STACK_END = 4;

export default {
  name: 'BeMerchantGallery',
  components: {
    CoolLightBox,
  },
  data() {
    return {
      activeIndex: null,
    };
  },
  computed: {
    ...mapGetters(['getMerchant']),
    images() {
      return this.getMerchant.merchant_images || [];
    },
    stackImages() {
      return this.images.slice(STACK_START, STACK_END);
    },
  },
  methods: {
    imageSrc(image) {
      return (image && image.image_url) || '';
    },
    isLastStack(index) {
      return index === this.stackImages.length - 1;
    },
    openLightbox(index) {
      this.activeIndex = index;
    },
  },
};
</script>

<style scoped>
.be-gallery-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 6px;
  border-radius: var(--be-v3-radius, 12px);
  overflow: hidden;
}
.be-gallery-main {
  grid-column: 1 / -1;
  height: 240px;
  overflow: hidden;
  cursor: pointer;
}
.be-gallery-secondary {
  height: 150px;
  overflow: hidden;
  cursor: pointer;
}
.be-gallery-stack {
  display: grid;
  grid-template-rows: 1fr 1fr;
  gap: 6px;
}
.be-gallery-stack-item {
  position: relative;
  overflow: hidden;
  cursor: pointer;
}
.be-gallery-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
}
.be-gallery-main:hover .be-gallery-img,
.be-gallery-secondary:hover .be-gallery-img,
.be-gallery-stack-item:hover .be-gallery-img,
.be-gallery-simple-item:hover .be-gallery-img {
  transform: scale(1.04);
}
.be-gallery-simple {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 6px;
  border-radius: var(--be-v3-radius, 12px);
  overflow: hidden;
}
.be-gallery-simple-item {
  position: relative;
  height: 260px;
  overflow: hidden;
  cursor: pointer;
}
.be-gallery-showall {
  position: absolute;
  bottom: 12px;
  right: 12px;
  display: inline-flex;
  align-items: center;
  background: #fff;
  color: #1a1a2e;
  border: none;
  border-radius: var(--be-v3-button-radius, 8px);
  padding: 8px 14px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}
@media screen and (max-width: 767.98px) {
  /* full-bleed hero: escape the page container's 15px gutters so the image runs
     edge to edge under the floating navbar. Anchored to the parent's own box
     (not 100vw) so it can't drift past the real screen edge and force page-wide
     horizontal scroll. */
  .be-gallery-grid,
  .be-gallery-simple {
    width: calc(100% + 30px);
    margin-left: -15px;
    margin-right: -15px;
    border-radius: 0;
    gap: 4px;
  }
  .be-gallery-grid {
    grid-template-columns: repeat(3, 1fr);
  }
  .be-gallery-main {
    height: 78vw;
    max-height: 340px;
  }
  /* the secondary image and the stacked pair line up as one strip of three thumbs;
     the stack's inner gap matches the outer one so all three end up equal width */
  .be-gallery-secondary,
  .be-gallery-stack {
    height: 21vw;
    max-height: 92px;
  }
  .be-gallery-stack {
    grid-column: span 2;
    grid-template-columns: 1fr 1fr;
    grid-template-rows: auto;
    gap: 4px;
  }
  /* the View all pill overhangs the thumb it lives in */
  .be-gallery-stack-item {
    overflow: visible;
  }
  .be-gallery-simple-item {
    height: 78vw;
    max-height: 340px;
  }
  .be-gallery-main:hover .be-gallery-img,
  .be-gallery-secondary:hover .be-gallery-img,
  .be-gallery-stack-item:hover .be-gallery-img,
  .be-gallery-simple-item:hover .be-gallery-img {
    transform: none;
  }
  .be-gallery-showall {
    border-radius: 999px;
    padding: 10px 20px;
    font-size: 15px;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.22);
  }
  /* centred on the thumb strip and flush with the screen edge; it is wider than the
     thumb it sits in, which is why the stack items drop their overflow clipping */
  .be-gallery-stack-item .be-gallery-showall {
    top: 50%;
    right: 0;
    bottom: auto;
    transform: translateY(-50%);
  }
}
@media screen and (min-width: 768px) {
  .be-gallery-grid {
    grid-template-columns: 1.6fr 1fr 1fr;
    align-items: stretch;
  }
  .be-gallery-main {
    grid-column: auto;
    height: 470px;
  }
  .be-gallery-secondary {
    height: 470px;
  }
  .be-gallery-stack {
    height: 470px;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/hotel/be/v3/BeMerchantGallery.vue